import _                                 from 'underscore';
import { MutableRefObject, useContext }  from 'react';
import moment, { Moment }                from 'moment';
import { ColumnInstance, TableInstance } from 'react-table';

import { CorvalAuditEntry, CorvalBusinessFunction, CorvalCategory, CorvalCostOccurenceType, CorvalCostOccurrenceResource, CorvalProjectBudgetItem, CorvalStaticProjectBudgetItem, CustomCostOccurrenceFeeItem, FeeBasedCostOccurrence, HireTypeOccurrence, OneOrArrayOf, ProjectActivityCostOccurrenceFeeItem, Uuid } from '../types';

import { applyPercentageToValue, maybeStaticVersionField, roundToPrecision, sum, sumByAttribute, updateDeep } from '../util';
import { ExtendedCorvalProjectBudgetItem, useFiltering, useProjectActivities, useProjectBudgetItems }         from '../hooks';
import { PreferenceStateType, usePrefStates }                                                                 from './usePrefStates';
import { ResourcesService, TimeService }                                                                      from '../services';
import { EMPTY_VALUE_DISPLAY }                                                                                from '../constants/FormatStringEnums';
import { BudgetVisualizationTableState }                                                                      from '../views/pages/projects/resource-planning/budgeting/BudgetVisualizationTableLayout';
import { BudgetByCategoryFilterState, BudgetVisualizationByCategoryTableData }                                from '../views/pages/projects/resource-planning/budgeting/by-category/BudgetVisualizationByCategory';
import { BudgetVisualizationByHireTableData }                                                                 from '../views/pages/projects/resource-planning/budgeting/by-hire/BudgetVisualizationByHire';
import { TimeframeDateControlValues }                                                                         from '../views/pages/projects/resource-planning/budgeting/by-category/MapToolFocusedDates';
import { ProjectContext }                                                                                     from "@src/views/pages/projects/ProjectIndexPage";


export enum ProjectBudgetVisualizationFilterSetPrefKey {
    BY_HIRE     = 'BUDGET_VISUALIZATION_BY_HIRE_ACTIVE_FILTERS',
    BY_CATEGORY = 'BUDGET_VISUALIZATION_BY_CATEGORY_ACTIVE_FILTERS',
}

export enum ProjectBudgetVisualizationActiveYearsPrefKey {
    BY_HIRE     = 'BUDGET_VISUALIZATION_BY_HIRE_ACTIVE_YEARS',
    BY_CATEGORY = 'BUDGET_VISUALIZATION_BY_CATEGORY_ACTIVE_YEARS',
}

export enum ProjectBudgetVisualizationYearTypes {
    ACTIVE,
    INACTIVE,
}

export type ColumnizedTotalData = [
    QuarterTotals: [
        Q1Total: number,
        Q2Total: number,
        Q3Total: number,
        Q4Total: number
    ], AnnualTotal: number
]

interface BudgetByCategoryCSVProcessingOptions {
    categoryTotalRows?: boolean,
    subcategoryTotalRows?: boolean,
    itemSetTotalRows?: boolean,
    itemTotalRows?: boolean,
    filters?: BudgetVisualizationFilterSetType,
}

interface BudgetByHireCSVProcessingOptions {
    filters?: BudgetVisualizationFilterSetType,
    resourceHireDateOccurrences?: HireTypeOccurrence[],
}

type CSVHeaderRowType = string[];
type CSVDataRowType = Array<string | number | null>;
type CSVDataValueRowType = Array<number | null>;

export interface UseProjectBudgetVisualizationHook<D> {

    getDefaultFilters( type?: ProjectBudgetVisualizationFilterSetPrefKey ): BudgetVisualizationFilterSetType,

    getInitialUserFilters( type?: ProjectBudgetVisualizationFilterSetPrefKey ): BudgetVisualizationFilterSetType,

    hasFiltersApplied( filterSetToCompare: Partial<BudgetVisualizationFilterSetType>, type?: ProjectBudgetVisualizationFilterSetPrefKey ): boolean,

    businessFunctionFilterMethod( filterValue: Uuid[], businessFunctions: Uuid[] ): boolean,

    tagFilterMethod( filterValue: string[], tags: string[] ): boolean,

    hireTypeFilterMethod( filterValue: Uuid[], hireTypes: Uuid[] ): boolean,

    getExpandedRows(): string[],

    isRowExpanded( rowId: string ): boolean,

    generateAnnualMapping<D>(
        startDate: Moment,
        endDate: Moment,
        items?: Array<D>,
        timestampAttribute?: string,
        sliceByTimeframe?: boolean,
    ): AnnualMappingItem<D>[],

    getAnnualMappingOutsideTimeframes(
        startDate: Moment,
        endDate: Moment,
        annualMapping: ReturnType<UseProjectBudgetVisualizationHook<D>['generateAnnualMapping']>
    ): AnnualMappingItem<D>[],

    generateQuarterColumnID( year: string | number, quarter: string | number ): string,

    generateYearlyColumnID( year: string | number ): string,

    getProjectYears(): number[],

    getYearsOfType( yearType: ProjectBudgetVisualizationYearTypes ): number[],

    getHiddenTimeColumns(): string[],

    getActiveYearsFromHiddenColumns( hiddenColumns: string[] ): number[],

    getQuarterlyColumns( year: number ): ColumnInstance<any>[],

    getQuarterlyColumnIds( year: number ): string[],

    handleYearExpansionToggled( year: number, setToHidden?: boolean ): void,

    handleAllYearExpansionToggled( hideAll: boolean ): void,

    isYearActive( year: number ): boolean,

    generateTotalsFromAnnualizedMapping(
        annualizedCostsPerYear: AnnualMappingItem<any>[],
        sumAttributePath: string | string[] | number[]
    ): Record<number, ColumnizedTotalData>,

    generateTotalOverQuartersUsingMethod( quarterStart, quarterEnd, callback ): number,

    hasCostAdjustments( filterSet: Partial<BudgetVisualizationFilterSetType> ): boolean,

    hasTimeframeRange( currentTimeframeRange: TimeframeDateControlValues<Moment | null> ): boolean,

    getProjectHireSubTotalForTimePeriod(
        resourceCostOccurrences: CorvalCostOccurrenceResource[],
        year: number,
        quarter?: number,
        filters?: BudgetByCategoryFilterState,
        compensationAdjustment?: number,
    ): number,

    getProjectCustomBudgetItemsSubTotalForTimePeriod(
        budgetItems: CorvalProjectBudgetItem[],
        year: number,
        quarter?: number,
        filters?: BudgetByCategoryFilterState,
        feeCostAdjustment?: number,
    ): number,

    rawCategoryBudgetDataToCSVFormat(
        budgetData: unknown[],
        hiringData: CorvalCostOccurrenceResource[],
        processingOptions?: BudgetByCategoryCSVProcessingOptions
    ): [ string, Array<CSVHeaderRowType | CSVDataRowType> ],

    rawHiringBudgetDataToCSVFormat(
        budgetData: BudgetVisualizationByHireTableData[],
        processingOptions?: BudgetByHireCSVProcessingOptions
    ): [ string, Array<CSVHeaderRowType | CSVDataRowType> ],
}

export interface AnnualMappingItem<D> {
    year: number,
    quarters: D[][],
    outsideTimeframe?: string[],
}

export interface ValuationAdjustmentFilterType {
    feeCosts?: number,
    operationalCosts?: number,
    scheduledCosts?: number,
    onGoingCosts?: number,
    headcountCost?: number,
    customDataCategoricalCosts?: number,
    customDataHiringCosts?: number,
}

export interface BudgetByHireFilterSetType {
    businessFunctions: Uuid[],
    tags: string[],
    dateRange: [ Moment | null, Moment | null ],
    costAdjustments: ValuationAdjustmentFilterType,
    hireTypes: Uuid[],
}

export interface BudgetByCategoryFilterSetType {
    businessFunctions: Uuid[],
    tags: string[],
    dateRange: [ Moment | null, Moment | null ],
    costAdjustments: ValuationAdjustmentFilterType,
    hireTypes: Uuid[],
}

export interface BudgetByCategoryCustomDataItem {
    id: Uuid,
    values: BudgetByCategoryCustomDataItemValue[],
    categoryId: Uuid,
    category?: Partial<CorvalCategory>
    subCategoryName: string,
    name: string,
    note: CorvalAuditEntry,
    businessFunction: CorvalBusinessFunction,
}

export interface BudgetByCategoryCustomDataItemValue {
    id: Uuid,
    value: number,
    startDate: string,
}

export type BudgetVisualizationFilterSetType = BudgetByHireFilterSetType | BudgetByCategoryFilterSetType;

export type BudgetVisualizationFilterSetTypeKeys =
    keyof BudgetByHireFilterSetType |
    keyof BudgetByHireFilterSetType['costAdjustments'] |
    keyof BudgetByCategoryFilterSetType |
    keyof BudgetByCategoryFilterSetType['costAdjustments'];

export type BudgetByCategoryCustomDataChangeCallback = ( customData: ExtendedCorvalProjectBudgetItem[] ) => void;

export const ProjectBudgetVisualizationByHireFilterSet: BudgetByHireFilterSetType = {
    businessFunctions: [],
    tags:              [],
    dateRange:         [ null, null ],
    costAdjustments:   {
        headcountCost:         0,
        customDataHiringCosts: 0,
    }
};

export const ProjectBudgetVisualizationByCategoryFilterSet: BudgetByCategoryFilterSetType = {
    businessFunctions: [],
    tags:              [],
    dateRange:         [ null, null ],
    costAdjustments:   {
        feeCosts:                   0,
        operationalCosts:           0,
        scheduledCosts:             0,
        onGoingCosts:               0,
        headcountCost:              0,
        customDataCategoricalCosts: 0,
        customDataHiringCosts:      0,
    },
    hireTypes:         [],

};

export function useProjectBudgetVisualization<D>(
    projectId: Uuid,
    tableInstance: MutableRefObject<TableInstance>,
    getVisualizationStartDate: () => Moment,
    getVisualizationEndDate: () => Moment,
    filterSetKey?: keyof PreferenceStateType,
    activeYearsPrefKey?: keyof PreferenceStateType,
){

    let { isCorvalManaged }             = useContext( ProjectContext ),
        [ prefState, prefs, prefEnums ] = usePrefStates( projectId ),
        { isFilterSetDefaultSet }       = useFiltering(),
        { getProjectActivityTags }      = useProjectActivities(),
        { getCustomBudgetItemTags }     = useProjectBudgetItems();

    const getDefaultFilters: UseProjectBudgetVisualizationHook<D>['getDefaultFilters'] = ( key = null ) => {

        switch( key ?? filterSetKey ){
            case ProjectBudgetVisualizationFilterSetPrefKey.BY_HIRE:
                return _.clone( ProjectBudgetVisualizationByHireFilterSet );

            case ProjectBudgetVisualizationFilterSetPrefKey.BY_CATEGORY:
                return _.clone( ProjectBudgetVisualizationByCategoryFilterSet );
        }

        return {} as BudgetVisualizationFilterSetType;
    };

    const getInitialUserFilters: UseProjectBudgetVisualizationHook<D>['getInitialUserFilters'] = ( key = null ) => {

        /* NOTE: Use with caution, as this currently appears to produce an initial pref set
         * when using multiple times in components, but is not updating when recalling again.
         * TODO: Find out why prefs are not always pulling from storage when called to get a key */

        // do we have any saved filters?
        let userFilterPrefs    = prefs.get( prefEnums[ key ?? filterSetKey ] ),
            defaultFilterPrefs = getDefaultFilters( key ),
            isDefaultSet       = isFilterSetDefaultSet( userFilterPrefs, defaultFilterPrefs );

        // get user's preferred filtering parameters, or default if empty.
        // merges all keys not found in user prefs but exist in the defaults into the user prefs object
        userFilterPrefs = ( _.isObject( userFilterPrefs ) && !isDefaultSet )
                          ? { ..._.omit( defaultFilterPrefs, _.keys( userFilterPrefs ) ), ...userFilterPrefs }
                          : defaultFilterPrefs;

        return userFilterPrefs;
    };

    const hasFiltersApplied: UseProjectBudgetVisualizationHook<D>['hasFiltersApplied'] = ( filterSetToCompare, key = null ) => {

        let defaultFilterPrefs = getDefaultFilters( key );

        return !isFilterSetDefaultSet( filterSetToCompare, defaultFilterPrefs );
    };

    const getExpandedRows: UseProjectBudgetVisualizationHook<D>['getExpandedRows'] = () => {

        const { expanded } = tableInstance.current.state as BudgetVisualizationTableState;

        // Array of row ids that are open / expanded
        return _.keys( expanded );
    };

    const isRowExpanded: UseProjectBudgetVisualizationHook<D>['isRowExpanded'] = ( rowId ) => {

        return getExpandedRows().includes( rowId );
    };

    /* Mapping method to construct an array of objects with year and quarter info give a start end date.
     Sorts items with a `startTimeUtc` attribute if available. */
    const generateAnnualMapping: UseProjectBudgetVisualizationHook<D>['generateAnnualMapping'] = ( startDate, endDate, items = [], timestampAttribute = 'startTimeUtc' ) => {

        /* Craft the years in the map */
        let rangeCount = ( endDate.year() - startDate.year() ) + 1, // preserve the end year
            rangeArray = _.range( rangeCount );

        let yearRange = rangeArray.map( ( slot, index ) => {
            return startDate.clone().startOf( 'year' ).add( index, 'years' );
        } );

        /* Create map */
        let mapping = yearRange.map( ( year: Moment ) => {

            let quarters = _.chain( items )
                            .filter( ( item ) => item[ timestampAttribute ].isSame( year, 'year' ) )   // only the activities in this year
                            .groupBy( ( item ) => `q${ item[ timestampAttribute ].quarter() }` )       // sort by date, timestamp-y
                            .value();

            return {
                year:     year.year(),
                quarters: [
                    quarters[ 'q1' ] ?? [],
                    quarters[ 'q2' ] ?? [],
                    quarters[ 'q3' ] ?? [],
                    quarters[ 'q4' ] ?? [],
                ],
            };
        } );

        // slice by timeframe if specified
        mapping = getAnnualMappingOutsideTimeframes( startDate, endDate, mapping );

        // sort by year
        mapping = _.sortBy( mapping, item => item.year );

        return mapping;

    };

    const getAnnualMappingOutsideTimeframes: UseProjectBudgetVisualizationHook<D>['getAnnualMappingOutsideTimeframes'] = ( startDate, endDate, annualMapping ) => {

        const slicedAnnualMapping = [];

        annualMapping.map( mapObject => {

            const curratedMapObject = _.clone( mapObject ),
                  outsideTimeframe  = [];

            curratedMapObject.quarters = curratedMapObject.quarters.map( ( quarterData, index ) => {

                const year         = mapObject.year,
                      quarter      = index + 1,
                      quarterStart = TimeService.dateFromYearAndQuarter( year, quarter ),
                      quarterEnd   = TimeService.dateFromYearAndQuarter( year, quarter ).endOf( 'quarter' );

                const quarterWithinTimeframe = TimeService.timerangeOverlapsTimeframe( quarterStart, quarterEnd, startDate, endDate, );

                if( !quarterWithinTimeframe ){

                    outsideTimeframe.push( `${ year }-${ quarter }` ); // stores as TimeService.dateRangeYearQuarterFormat format
                }

                return quarterData;

            } );

            if( curratedMapObject.quarters.length > 0 ){

                slicedAnnualMapping.push( { ...curratedMapObject, outsideTimeframe } );
            }

        } );

        return slicedAnnualMapping;
    };

    /* Generates an annual column id */
    const generateQuarterColumnID: UseProjectBudgetVisualizationHook<D>['generateQuarterColumnID'] = ( year: string | number, quarter: string | number ) => {

        return `${ year }-${ quarter }`;
    };

    /* Generates a year's quarter column id */
    const generateYearlyColumnID: UseProjectBudgetVisualizationHook<D>['generateYearlyColumnID'] = ( year: string | number ) => {

        return `${ year }`;
    };

    /* Retrieves all years with the start / end dates specified */
    const getProjectYears: UseProjectBudgetVisualizationHook<D>['getProjectYears'] = () => {

        return _.pluck( generateAnnualMapping<D>(
            getVisualizationStartDate() ?? moment(),
            getVisualizationEndDate() ?? moment(),
            []
        ), 'year' );

    };

    /* Retrieves the years of a certain type, based on annual mapping method and what user has as preferred active years. */
    const getYearsOfType: UseProjectBudgetVisualizationHook<D>['getYearsOfType'] = ( yearType: ProjectBudgetVisualizationYearTypes = null ) => {

        let years       = getProjectYears(),
            activeYears = prefs.get( activeYearsPrefKey ) ?? [];

        switch( yearType ){
            case ProjectBudgetVisualizationYearTypes.ACTIVE:
                return _.intersection( years, activeYears ) as number[];
            case ProjectBudgetVisualizationYearTypes.INACTIVE:
                return _.difference( years, activeYears ) as number[];
        }

        return [];

    };

    /* Retrieves the hidden columns not active, based on active years. */
    const getHiddenTimeColumns: UseProjectBudgetVisualizationHook<D>['getHiddenTimeColumns'] = () => {

        let inactiveYears = getYearsOfType( ProjectBudgetVisualizationYearTypes.INACTIVE );

        return _.chain( inactiveYears )
                .map( year => _.range( 1, 5 ).map( quarter => generateQuarterColumnID( year, quarter ) ) )
                .union()
                .flatten()
                .value() ?? [];

    };

    const getActiveYearsFromHiddenColumns: UseProjectBudgetVisualizationHook<D>['getActiveYearsFromHiddenColumns'] = ( hiddenColumns ) => {

        let projectYears      = getProjectYears().map( y => `${ y }` ),
            hiddenYearColumns = _.chain( hiddenColumns )
                                 .map( c => c.split( '-' )[ 0 ] )
                                 .filter( value => projectYears.includes( value ) )
                                 .uniq()
                                 .value();

        return _.chain( projectYears )
                .difference( hiddenYearColumns )
                .map( year => _.isNaN( parseInt( year ) ) ? null : parseInt( year ) )
                .compact()
                .value();

    };

    /* Retrieves all column instance objects for a given year */
    const getQuarterlyColumns: UseProjectBudgetVisualizationHook<D>['getQuarterlyColumns'] = ( year: number ): ColumnInstance<any>[] => {

        let columnsInGroup   = _.findWhere( tableInstance.current.columns, { id: `${ year }-group` } )?.columns ?? [],
            quarterlyColumns = _.first( columnsInGroup, 4 );

        return quarterlyColumns;
    };

    /* Retrieves all column ids for a given year */
    const getQuarterlyColumnIds: UseProjectBudgetVisualizationHook<D>['getQuarterlyColumnIds'] = ( year: number ): string[] => {

        let quarterlyColumns   = getQuarterlyColumns( year ),
            quarterlyColumnIDs = _.pluck( quarterlyColumns, 'id' );

        return quarterlyColumnIDs;
    };

    /* Handler method to expand or collapse tableInstance year column by setting hidden columns */
    const handleYearExpansionToggled: UseProjectBudgetVisualizationHook<D>['handleYearExpansionToggled'] = ( year: number = null, setToHidden: boolean = null ) => {

        let quarterlyColumns   = getQuarterlyColumns( year ),
            quarterlyColumnIDs = getQuarterlyColumnIds( year );

        let columnsAreVisible: unknown,
            userActiveYears = getActiveYearsFromHiddenColumns( tableInstance.current.state.hiddenColumns );

        if( _.isNull( setToHidden ) ){

            // Since all years collapse at once, just check for the first one's checked state
            columnsAreVisible = !( quarterlyColumns[ 0 ]?.getToggleHiddenProps().checked ?? false );

        } else {

            // we have specified if we should hide or not, so use that instead of deriving
            columnsAreVisible = !setToHidden;

        }

        tableInstance.current.setHiddenColumns( ( hiddenColumns ) => (
            columnsAreVisible
            ? _.difference( hiddenColumns, quarterlyColumnIDs )
            : _.union( hiddenColumns, quarterlyColumnIDs )
        ) );

        // update prefs
        if( activeYearsPrefKey ){

            prefs.update( activeYearsPrefKey,
                columnsAreVisible
                ? [ ...userActiveYears, year ]
                : _.without( userActiveYears, year )
            );
        }
    };

    /* Handler method to expand or collapse all tableInstance yearls columns by setting hidden columns */
    const handleAllYearExpansionToggled: UseProjectBudgetVisualizationHook<D>['handleAllYearExpansionToggled'] = ( hideAll: boolean = false ) => {

        let allProjectYears    = getProjectYears(),
            quarterlyColumnIDs = _.flatten( allProjectYears.map( getQuarterlyColumnIds ) );

        tableInstance.current.setHiddenColumns( ( hiddenColumns ) => (
            hideAll
            ? _.union( hiddenColumns, quarterlyColumnIDs )
            : _.difference( hiddenColumns, quarterlyColumnIDs )
        ) );

        // update prefs
        if( activeYearsPrefKey ){

            prefs.update( activeYearsPrefKey, hideAll ? [] : allProjectYears );
        }
    };

    /* Method to determine quickly if a given year is visible / active */
    const isYearActive: UseProjectBudgetVisualizationHook<D>['isYearActive'] = ( year: number ) => {

        return getActiveYearsFromHiddenColumns( tableInstance.current.state.hiddenColumns ).includes( year );
    };

    const generateTotalsFromAnnualizedMapping: UseProjectBudgetVisualizationHook<D>['generateTotalsFromAnnualizedMapping'] = (
        annualizedCostsPerYear: AnnualMappingItem<unknown>[],
        sumAttributePath = [ 0 ]
    ) => {

        let totals = {};

        annualizedCostsPerYear.map( ( year, yearIndex ) => {

            let yearlyTotals: ColumnizedTotalData = [ [ 0, 0, 0, 0 ], 0 ];

            year.quarters.map( ( itemsInQuarter, quarterIndex ) => {

                let normalizedItemsInQuarter = itemsInQuarter.map( itemInQuarter => {

                    let totalsAttribute = _.property( sumAttributePath )( itemInQuarter );

                    // If this is an array of cost data, find the totalizationType value
                    // and update the property accordingly
                    if( _.isArray( totalsAttribute ) && totalsAttribute.length === 2 ){

                        return updateDeep(
                            itemInQuarter,
                            sumAttributePath,
                            ResourcesService.getCostValueFromCostData( totalsAttribute ),
                            true
                        );
                    }

                    return itemInQuarter;
                } );

                yearlyTotals[ 0 ][ quarterIndex ] = sumByAttribute<unknown>( normalizedItemsInQuarter, sumAttributePath );

            } );

            // total this row up for each annualized column
            yearlyTotals[ 1 ] = _.reduce( yearlyTotals[ 0 ], ( sum, qCost ) => ( sum + qCost ), 0 );

            totals[ year.year ] = yearlyTotals;
        } );

        return totals;
    };

    const generateTotalOverQuartersUsingMethod: UseProjectBudgetVisualizationHook<D>['generateTotalOverQuartersUsingMethod'] = ( quarterStart = 1, quarterEnd = 4, callback ) => {

        return sum( _.range( quarterStart, quarterEnd + 1 ).map( q => callback( q ) ) );
    };

    const hasCostAdjustments: UseProjectBudgetVisualizationHook<D>['hasCostAdjustments'] = ( gridFilters ) => {

        return gridFilters ? sum( _.values( gridFilters.costAdjustments ), 0 ) !== 0 : false;
    };

    const hasTimeframeRange: UseProjectBudgetVisualizationHook<D>['hasTimeframeRange'] = ( currentTimeframeRange ) => {

        return TimeService.hasTimeframeRange( currentTimeframeRange );
    };


    const getProjectHireSubTotalForTimePeriod: UseProjectBudgetVisualizationHook<any>['getProjectHireSubTotalForTimePeriod'] = (
        resourceCostOccurrences: CorvalCostOccurrenceResource[],
        year: number,
        quarter: number        = null,
        filters                = null,
        compensationAdjustment = 0,
    ): number => {

        let isFiltering         = ( filters && filters.isFiltering ),
            useQuarterlyFocused = !_.isNull( quarter ),
            timestampStart      = useQuarterlyFocused
                                  ? moment( `${ quarter }${ year }`, 'QYYYY' )
                                  : moment( year, 'YYYY' ),
            timestampEnd        = useQuarterlyFocused
                                  ? timestampStart.clone().add( 1, 'quarter' )
                                  : timestampStart.clone().add( 1, 'year' );

        const projectHireField = maybeStaticVersionField( 'projectHire', !isCorvalManaged );

        if( resourceCostOccurrences &&
            resourceCostOccurrences.length ){

            let applicableHires = _.filter( resourceCostOccurrences, hire => {

                // get the hire date and business function
                let hireDate         = TimeService.ISOtoMoment( hire.resourceCostOccurrence ),
                    businessFunction = hire[ projectHireField ].hireType.businessFunction?.id,
                    hireTypes        = hire.hireTypeId;


                // is this hire between the timestamps, and includes the filters set (if filtering)?
                let isInTimePeriod      = hireDate.isBetween( timestampStart, timestampEnd, useQuarterlyFocused ? 'quarter' : 'year', '[)' ),
                    hasBusinessFunction = ( isFiltering && filters.isFilteringBusinessFunction ) ? filters.filterValues.businessFunctions.includes( businessFunction ) : true,
                    hasHireTypes        = ( isFiltering && filters.isFilteringHireTypes ) ? filters.filterValues.hireTypes.includes( hireTypes ) : true;

                // is this value between any bounded timestamps inclusive?
                let isWithinTimeFrame                    = true,
                    [ boundayStartDate, boundayEndDate ] = ( isFiltering && filters.isFilteringDateRange ) ? filters.filterValues.dateRange : [ null, null ];

                if( boundayStartDate && boundayEndDate && timestampStart && timestampEnd ){

                    isWithinTimeFrame = TimeService.timerangeOverlapsTimeframe( timestampStart, timestampEnd, boundayStartDate, boundayEndDate );
                }

                return ( isInTimePeriod && isWithinTimeFrame && hasBusinessFunction && hasHireTypes );
            } );

            if( applicableHires ){

                let total = sumByAttribute( applicableHires, 'quarterlyCompensation' );

                // Adjust total cost
                if( compensationAdjustment ){

                    total = applyPercentageToValue( total, compensationAdjustment / 100 );
                }

                return total;
            }
        }

        return 0;

    };

    const getProjectCustomBudgetItemsSubTotalForTimePeriod: UseProjectBudgetVisualizationHook<any>['getProjectCustomBudgetItemsSubTotalForTimePeriod'] = (
        budgetItems: CorvalProjectBudgetItem[] | CorvalStaticProjectBudgetItem[],
        year: number,
        quarter: number   = null,
        filters           = null,
        feeCostAdjustment = 0,
    ): number => {

        // @TODO: Custom data does not support tags; refactor when they do.

        let isFiltering         = ( filters && filters.isFiltering ),
            useQuarterlyFocused = !_.isNull( quarter ),
            timestampStart      = useQuarterlyFocused
                                  ? moment( `${ quarter }${ year }`, 'QYYYY' )
                                  : moment( year, 'YYYY' ),
            timestampEnd        = useQuarterlyFocused
                                  ? timestampStart.clone().add( 1, 'quarter' )
                                  : timestampStart.clone().add( 1, 'year' );

        if( budgetItems && budgetItems.length ){

            let applicableBudgetValues = _.chain( budgetItems )
                                          .map( item => {

                                              // filter out items that do not meet filtering criteria
                                              if( isFiltering ){

                                                  // get the business functions
                                                  let hasBusinessFunction = businessFunctionFilterMethod( filters.filterValues.businessFunctions, [ item.businessFunction.id ] );

                                                  // get the tags
                                                  let hasTags = tagFilterMethod( filters.filterValues.tags, getCustomBudgetItemTags( item ) );

                                                  if( !hasBusinessFunction || !hasTags ) return null;
                                              }

                                              // filter out items that do not fit within the timeframe
                                              return item.values.map( v => ( {
                                                  value: v.value,
                                                  date:  isCorvalManaged
                                                         ? TimeService.ISOtoDisplayDate( ( v as CorvalProjectBudgetItem['values'][0] ).startTimeUtc, true )
                                                         : TimeService.localDateToDisplayDate( ( v as CorvalStaticProjectBudgetItem['values'][0] ).startDate )
                                              } ) )
                                                         .filter( ( { value, date } ) => {

                                                             // is this value between the timestamps inclusive, and includes the filters set (if filtering)?
                                                             let isInTimePeriod = moment( date, TimeService.getDisplayDateFormat() ).isBetween( timestampStart, timestampEnd, useQuarterlyFocused ? 'quarter' : 'year', '[)' );

                                                             // is this value between any bounded timestamps inclusive?
                                                             let isWithinTimeFrame                    = true,
                                                                 [ boundayStartDate, boundayEndDate ] = ( isFiltering ) ? filters.filterValues.dateRange : [ null, null ];

                                                             if( boundayStartDate && boundayEndDate && timestampStart && timestampEnd ){

                                                                 isWithinTimeFrame = TimeService.timerangeOverlapsTimeframe( timestampStart, timestampEnd, boundayStartDate, boundayEndDate );
                                                             }

                                                             return ( isInTimePeriod && isWithinTimeFrame );

                                                         } );

                                          } )
                                          .flatten()
                                          .compact()
                                          .value();

            if( applicableBudgetValues ){

                return _.reduce( applicableBudgetValues, ( sum, valueObj ) => {

                    // apply any headcount adjustments
                    let adjustedValue = valueObj.value + ( valueObj.value * ( feeCostAdjustment / 100 ) );

                    return sum + adjustedValue;

                }, 0 );
            }
        }

        return 0;

    };

    const businessFunctionFilterMethod = ( filterValue: Uuid[] = [], businessFunctions: Uuid[] = [] ) => {
        return filterValue.length > 0 ? _.intersection( filterValue, businessFunctions ).length > 0 : true;
    };

    const tagFilterMethod = ( filterValue: string[] = [], tags: string[] = [] ) => {
        return filterValue.length > 0 ? _.intersection( filterValue, tags ).length > 0 : true;
    };

    const hireTypeFilterMethod = ( filterValue: Uuid[] = [], hireTypes: Uuid[] = [] ) => {
        return filterValue.length > 0 ? _.intersection( filterValue, hireTypes ).length > 0 : true;
    };

    const rawCategoryBudgetDataToCSVFormat: UseProjectBudgetVisualizationHook<D>['rawCategoryBudgetDataToCSVFormat'] = (
        budgetData: BudgetVisualizationByCategoryTableData[],
        hiringData: CorvalCostOccurrenceResource[],
        userOptions = {}
    ) => {

        let CSVRows               = [] as Array<CSVDataRowType | CSVHeaderRowType>,
            CSVRowTotals          = [] as Array<CSVDataValueRowType>,
            CSVDEMRowTotals       = [] as Array<CSVDataValueRowType>,
            CSVDEMRowSubtotals    = [] as Array<CSVDataValueRowType>,
            CSVCustomRowTotals    = [] as Array<CSVDataValueRowType>,
            CSVCustomRowSubtotals = [] as Array<CSVDataValueRowType>,
            CSVData;

        let defaultProcessingOptions = {
            categoryTotalRows:    false,
            subcategoryTotalRows: false,
            itemSetTotalRows:     false,
            itemTotalRows:        true,
            filters:              null
        } as BudgetByCategoryCSVProcessingOptions;

        let processingOptions = { ...defaultProcessingOptions, ...userOptions },
            emptyValue: null  = null;

        // Method to scrub CSV sensitive string characters
        let formatLabelString = ( str: any ) => `"${ str }"` as string;

        // Method to expand table data totals into a flat array
        let expandCostOccurrenceTotals = ( data: BudgetVisualizationByCategoryTableData, instanceType: CorvalCostOccurenceType = null ) => {

            let inspectTypes              = !_.isNull( instanceType ),
                hasDiscreetTypesAvailable = data?.totalsDiscreet ?? false;

            let totalsProcessingMethod = ( dataTotals: BudgetVisualizationByCategoryTableData['totals'] ) => {

                return _.chain( dataTotals ).values()
                        .map( ( rowTotal: OneOrArrayOf<number>[] ) => _.flatten( rowTotal ) ).flatten()
                        .map( total => total === 0 ? null : roundToPrecision( total, 0 ) )
                        .value();
            };

            // if inspecting types...
            if( inspectTypes ){

                if( hasDiscreetTypesAvailable ){

                    let discreetValueTotals = _.find( data.totalsDiscreet, ( discreetTotal ) => {
                        return discreetTotal.instanceTypes.includes( instanceType );
                    } );

                    // discretely expand from the set that has the instance type
                    return totalsProcessingMethod( discreetValueTotals.totals );

                } else {

                    return null;
                }
            }

            // otherwise expand the whole thing
            return totalsProcessingMethod( data.totals );
        };

        let isCustomData = ( co: FeeBasedCostOccurrence ) => ResourcesService.isCustomDataCostOccurrence( co );
        let getOccurrenceDataItem = ( co: FeeBasedCostOccurrence ) => co.itemData;

        let businessFunctionsToFilterBy  = processingOptions?.filters?.businessFunctions ?? [],
            tagsToFilterBy               = processingOptions?.filters?.tags ?? [],
            dateRangeToFilterBy          = processingOptions?.filters?.dateRange ?? [ null, null ],
            costAdjustments              = processingOptions?.filters?.costAdjustments ?? null,
            hasBusinessFunctionFiltering = ( businessFunctionsToFilterBy.length > 0 ),
            hasTagsFiltering             = ( tagsToFilterBy.length > 0 ),
            hasDateRangeFiltering        = hasTimeframeRange( dateRangeToFilterBy ),
            isFiltering                  = ( hasTagsFiltering || hasBusinessFunctionFiltering || hasDateRangeFiltering ),
            hasCostAdjustments           = _.isObject( costAdjustments );

        // Generate a year / quarter reference array
        let years               = _.keys( _.first( budgetData ).totals ),
            yearSlots: string[] = _.flatten( years.map( year => [ ..._.range( 4 ).map( q => `${ year }-${ q + 1 }` ), year ] ) );

        // Headers
        let CSVHeaders: CSVHeaderRowType = [
            'Row Type',
            'Business Function',
            'Category',
            'Subcategory',
            'Objective / Custom Group',
            'Activity / Custom Item',
            'Tags',
            ...yearSlots.map( slotLabel => slotLabel.split( '-' ).join( ' Q' ) )
        ];

        CSVRows.push( CSVHeaders );

        // Filtering
        let costOccurrenceFiltering = ( co: FeeBasedCostOccurrence ) => {

            let businessFunction = getOccurrenceDataItem( co ).getBusinessFunction();

            // @TODO: Custom data does not support tags
            let tags = isCustomData( co )
                       ? getCustomBudgetItemTags( getOccurrenceDataItem( co ).data )
                       : getProjectActivityTags( getOccurrenceDataItem( co ).data );

            // Check business function
            let hasBusinessFunction = ( hasBusinessFunctionFiltering )
                                      ? businessFunctionFilterMethod( businessFunctionsToFilterBy, [ businessFunction.id ] )
                                      : true;

            // Check tags
            let hasTags = ( hasTagsFiltering )
                          ? tagFilterMethod( tagsToFilterBy, tags )
                          : true;

            return ( hasBusinessFunction && hasTags );
        };

        // Data Rows
        budgetData.map( category => {

            let categoryName                    = formatLabelString( category.name ),
                categoryTotals                  = expandCostOccurrenceTotals( category ),
                categoryRowData: CSVDataRowType = [ 'Category', categoryName, emptyValue, emptyValue, emptyValue, ...categoryTotals ];

            // Push category totals row
            if( processingOptions.categoryTotalRows ){
                CSVRows.push( categoryRowData );
            }

            // push category totals
            // CSVRowTotals.push( categoryTotals );

            // push discreet category totals
            CSVDEMRowTotals.push( expandCostOccurrenceTotals( category, CorvalCostOccurenceType.Projectactivity ) );
            CSVCustomRowTotals.push( expandCostOccurrenceTotals( category, CorvalCostOccurenceType.Customuser ) );

            // Generate subcategory rows
            category.subRows.map( subcategory => {

                let subcategoryName                    = formatLabelString( subcategory.name ),
                    subcategoryTotals                  = expandCostOccurrenceTotals( subcategory ),
                    subcategoryRowData: CSVDataRowType = [ 'Subcategory', categoryName, subcategoryName, emptyValue, emptyValue, ...subcategoryTotals ];

                // Push subcategory totals row
                if( processingOptions.subcategoryTotalRows ){
                    CSVRows.push( subcategoryRowData );
                }

                // Generate objective rows
                subcategory.subRows.map( itemSet => {

                    let delegateOccurrence = _.first( itemSet.costOccurrences ),
                        itemSetName        = formatLabelString( itemSet.name ),
                        itemSetType        = ( itemSet.costOccurrences.length )
                                             ? isCustomData( delegateOccurrence )
                                               ? 'Custom Group'
                                               : 'Objective'
                                             : 'Item Set';

                    let itemSetTotals                  = expandCostOccurrenceTotals( itemSet ),
                        itemSetRowData: CSVDataRowType = [ itemSetType, categoryName, subcategoryName, itemSetName, emptyValue, ...itemSetTotals ];

                    // Push itemSet row
                    if( processingOptions.itemSetTotalRows ){
                        CSVRows.push( itemSetRowData );
                    }

                    // If we have cost occurrences, prepare them
                    if( processingOptions.itemTotalRows && itemSet.costOccurrences.length ){

                        // Group cost occurrences by itemization
                        let costOccurrencesByItemId = _.chain( itemSet.costOccurrences )
                                                       .filter( costOccurrenceFiltering )
                                                       .sortBy( co => co.costOccurrence.format( TimeService.getYearFirstDisplayDateFormat() ) )
                                                       .groupBy( ( co: FeeBasedCostOccurrence ) => {
                                                           return ( isCustomData( co ) )
                                                                  ? getOccurrenceDataItem( co as CustomCostOccurrenceFeeItem ).getId()
                                                                  : getOccurrenceDataItem( co as ProjectActivityCostOccurrenceFeeItem ).getActivityId();
                                                       } )
                                                       .values()
                                                       .value();

                        // Generate itemization rows
                        costOccurrencesByItemId.map( ( costOccurrences: FeeBasedCostOccurrence[] ) => {

                            let delegateOccurrence = _.first( costOccurrences ),
                                itemName           = getOccurrenceDataItem( delegateOccurrence ).getName();

                            let businessFunction = getOccurrenceDataItem( delegateOccurrence ).getBusinessFunction();

                            // all tags for this set
                            let tags = isCustomData( delegateOccurrence )
                                       ? getCustomBudgetItemTags( getOccurrenceDataItem( delegateOccurrence ).data )
                                       : getProjectActivityTags( getOccurrenceDataItem( delegateOccurrence ).data );

                            let yearSlotsHash = yearSlots.map( ( slot, ordinal ) => ( { slot, ordinal, value: null } ) ) as Array<{ slot: string, ordinal: number, value: null | number }>;

                            // Slot costs into the yearly hash
                            costOccurrences.map( item => {

                                let slot      = item.costOccurrence.format( 'YYYY-Q' ),
                                    value     = roundToPrecision( item.costAmount ),
                                    slotIndex = _.findIndex( yearSlotsHash, { slot } );

                                // aggregate values instead overwriting the value
                                let totalValue = ( yearSlotsHash[ slotIndex ].value ?? 0 ) + value;

                                // add total value into slot
                                yearSlotsHash[ slotIndex ] = { ...yearSlotsHash[ slotIndex ], value: totalValue };

                            } );

                            // Aggregate slot costs
                            years.map( year => {

                                // aggregate activity yearly total
                                let yearlyTotal = sumByAttribute( _.filter( yearSlotsHash, timeSlot => {

                                    return timeSlot.slot.includes( year ) && ( timeSlot.slot != year );

                                } ), 'value' );

                                // is we have a positive total, add into the hash
                                if( yearlyTotal > 0 ){

                                    let slotIndex = _.findIndex( yearSlotsHash, { slot: year } );

                                    yearSlotsHash[ slotIndex ] = { ...yearSlotsHash[ slotIndex ], value: yearlyTotal };
                                }

                            } );

                            let formattedItemName = formatLabelString( itemName ),
                                itemRowName       = isCustomData( delegateOccurrence ) ? 'Custom Item' : 'Activity',
                                itemRowValues     = _.map( yearSlotsHash, slot => slot.value ),
                                itemRowData       = [ itemRowName, businessFunction.name, categoryName, subcategoryName, itemSetName, formattedItemName, `"${ tags.join( ',' ) }"`, ...itemRowValues ] as CSVDataRowType;

                            // Push item row
                            CSVRows.push( itemRowData );

                            // Push discreet totals row
                            isCustomData( delegateOccurrence )
                            ? CSVCustomRowSubtotals.push( itemRowValues )
                            : CSVDEMRowSubtotals.push( itemRowValues );
                        } );
                    }
                } );
            } );
        } );

        // Aggregate all totals
        let feeTotals        = _.range( yearSlots.length ).map( ( total, index ) => sum( CSVDEMRowTotals.map( t => t[ index ] ) ) ),
            customDataTotals = _.range( yearSlots.length ).map( ( total, index ) => sum( CSVCustomRowTotals.map( t => t[ index ] ) ) );

        // Aggregate professional fee totals
        let feeSubtotals = _.range( yearSlots.length ).map( ( total, index ) => sum( CSVDEMRowSubtotals.map( t => t[ index ] ) ) );

        // Aggregate custom data totals
        let customSubtotals = _.range( yearSlots.length ).map( ( total, index ) => sum( CSVCustomRowSubtotals.map( t => t[ index ] ) ) );

        // Aggregate hiring totals
        let hiringTotals = yearSlots.map( yearSlot => {

            let [ year, quarter ] = yearSlot.split( '-' );

            let hiringYear            = parseInt( year ),
                hiringQuarter         = quarter ? parseInt( quarter ) : null,
                hiringCostAdjustments = hasCostAdjustments ? costAdjustments.headcountCost : 0,
                subTotal              = 0;

            const [ startDate, endDate ] = dateRangeToFilterBy,
                  compareStartDate       = quarter ? TimeService.dateFromYearAndQuarter( hiringYear, hiringQuarter ) : TimeService.dateFromYearAndQuarter( hiringYear ),
                  compareEndDate         = quarter ? TimeService.dateFromYearAndQuarter( hiringYear, hiringQuarter ).endOf( 'quarter' ) : TimeService.dateFromYearAndQuarter( hiringYear ).endOf( 'year' ),
                  timeframeValid         = TimeService.timerangeOverlapsTimeframe( compareStartDate, compareEndDate, startDate, endDate, );

            if( ( hasDateRangeFiltering && timeframeValid ) || !( hasDateRangeFiltering ) ){

                subTotal = getProjectHireSubTotalForTimePeriod(
                    hiringData,
                    hiringYear,
                    hiringQuarter, {
                        isFiltering,
                        isFilteringBusinessFunction: hasBusinessFunctionFiltering,
                        isFilteringTags:             hasTagsFiltering,
                        isFilteringDateRange:        hasDateRangeFiltering,
                        filterValues:                processingOptions.filters
                    },
                    hiringCostAdjustments, );
            }

            return roundToPrecision( subTotal, 0 );
        } );

        // Aggregate totals
        let subtotals = _.range( yearSlots.length ).map( ( total, index ) => sum( [ feeSubtotals, customSubtotals, hiringTotals ].map( t => t[ index ] ) ) );
        let totals = _.range( yearSlots.length ).map( ( total, index ) => sum( [ feeTotals, customDataTotals, hiringTotals ].map( t => t[ index ] ) ) );

        // // push professional fee total / subtotal row
        // CSVRows.push( [ 'Fee Totals', emptyValue, emptyValue, emptyValue, emptyValue, emptyValue, emptyValue, ...( isFiltering ? feeSubtotals : feeTotals ) ] );
        //
        // // push custom data total / subtotal row
        // CSVRows.push( [ 'Custom Data Totals', emptyValue, emptyValue, emptyValue, emptyValue, emptyValue, emptyValue, ...( isFiltering ? customSubtotals : customDataTotals ) ] );
        //
        // // push hiring total / subtotal row
        // CSVRows.push( [ 'Hiring Totals', emptyValue, emptyValue, emptyValue, emptyValue, emptyValue, emptyValue, ...hiringTotals ] );
        //
        // // push total / subtotal row
        // CSVRows.push( [ 'Totals', emptyValue, emptyValue, emptyValue, emptyValue, emptyValue, emptyValue, ...( isFiltering ? subtotals : totals ) ] );

        // stringify rows
        CSVData = CSVRows.join( '\n' );

        return [ CSVData, CSVRows ];
    };

    const rawHiringBudgetDataToCSVFormat: UseProjectBudgetVisualizationHook<D>['rawHiringBudgetDataToCSVFormat'] = (
        budgetData: BudgetVisualizationByHireTableData[],
        userOptions = {}
    ) => {

        let CSVRows         = [] as Array<CSVDataRowType | CSVHeaderRowType>,
            CSVRowTotals    = [] as Array<CSVDataValueRowType>,
            CSVRowSubtotals = [] as Array<CSVDataValueRowType>,
            CSVData;

        const projectHireField     = maybeStaticVersionField( 'projectHire', !isCorvalManaged ),
              projectActivityField = maybeStaticVersionField( 'projectActivity', !isCorvalManaged );


        let defaultProcessingOptions = {
            filters: null
        } as BudgetByHireCSVProcessingOptions;

        let processingOptions = { ...defaultProcessingOptions, ...userOptions },
            emptyValue: null  = null;

        // Sort resources by
        let sortedBudgetData = _.sortBy( budgetData, ( item ) => `${ item.businessFunction.id }-${ item.name }` );

        // Method to scrub CSV sensitive string characters
        let formatLabelString = ( str: any ) => `"${ str }"` as string;

        let businessFunctions            = processingOptions?.filters?.businessFunctions ?? [],
            costAdjustments              = processingOptions?.filters?.costAdjustments ?? null,
            hasBusinessFunctionFiltering = businessFunctions.length > 0,
            hasCostAdjustments           = _.isObject( costAdjustments );

        // Generate a year / quarter reference array
        const shouldNarrowTimeFrame                = hasTimeframeRange( userOptions.filters.dateRange ),
              [ focusedStartDate, focusedEndDate ] = userOptions.filters.dateRange;

        const years = ( shouldNarrowTimeFrame )
                      ? _.range( TimeService.ISOtoMoment( focusedStartDate ).year(), TimeService.ISOtoMoment( focusedEndDate ).year() + 1 ).map( year => `${ year }` )
                      : _.keys( _.first( sortedBudgetData ).totals );

        const yearSlots: string[] = _.flatten( years.map( year => [ ..._.range( 4 ).map( q => `${ year }-${ q + 1 }` ), year ] ) );

        // Headers
        let CSVHeaders: CSVHeaderRowType = [
            'Row Type',
            'Business Function',
            'Category',
            'Subcategory',
            'Hire Type',
            'Hire Date',
            'Starting Year',
            'Starting Quarter',
            'Quarter Count',
            'Quarterly Compensation Per Hire',
            'Quantity',
            'Activity',
            'Notes',
            ...yearSlots.map( slotLabel => slotLabel.split( '-' ).join( ' Q' ) )
        ];

        CSVRows.push( CSVHeaders );

        // Filtering
        let costOccurrenceFiltering = ( co: HireTypeOccurrence ) => {

            return ( hasBusinessFunctionFiltering )
                   ? businessFunctions.includes( co.hireTypeBusinessFunctionId )
                   : true;
        };

        // Method to expand table data totals into a flat array
        let expandCostOccurrenceTotals = ( data: BudgetVisualizationByHireTableData ) => {

            return _.chain( data.totals )
                    .values()
                    .map( ( rowTotal: OneOrArrayOf<number>[] ) => _.flatten( rowTotal ) )
                    .flatten()
                    .map( total => total === 0 ? null : roundToPrecision( total ) )
                    .value();
        };

        // Method to total each data column within a given set of data rows
        let totalColumnizedValuesByYearSlot = ( data: Array<CSVDataValueRowType> ) => {

            return _.range( yearSlots.length ).map( ( total, index ) => sum( data.map( t => t[ index ] ) ) );
        };

        // Data Rows
        sortedBudgetData.map( hireType => {

            let { projectHires } = hireType;

            // If we have cost occurrences, prepare them
            if( hireType.hireOccurrences.length ){

                // Group occurrences by each project hire
                let costOccurrencesByProjectHire = _.chain( hireType.hireOccurrences )
                                                    .filter( costOccurrenceFiltering )
                                                    .sortBy( co => co.resourceCostOccurrence.format( TimeService.getYearFirstDisplayDateFormat() ) )
                                                    .groupBy( co => co[ projectHireField ].id )
                                                    .values()
                                                    .value();

                // Generate 'occurrence costs by project hire' rows, per projectHire
                costOccurrencesByProjectHire.map( ( hireTypeOccurrences: HireTypeOccurrence[] ) => {

                    // Create a year hash to track occurrences into
                    let yearSlotsHash = yearSlots.map( ( slot, ordinal ) => ( { slot, ordinal, value: null } ) ) as Array<{ slot: string, ordinal: number, value: null | number }>;

                    let delegateHire                            = _.first( hireTypeOccurrences ),
                        hiringDate                              = TimeService.asDisplayDate( delegateHire[ projectHireField ].hireDate ),
                        businessFunction                        = delegateHire.hireTypeBusinessFunction,
                        projectActivity                         = delegateHire[ projectHireField ][ projectActivityField ],
                        isUserDefined                           = _.isNull( projectActivity ),
                        { category = null, subCategory = null } =
                            !isUserDefined
                            ? !isCorvalManaged
                              ? {
                                    category:    { displayName: projectActivity.categoryName },
                                    subCategory: { displayName: projectActivity.subCategoryName },
                                }
                              : projectActivity.activity
                            : {};

                    // Slot quarterly costs into the yearly hash
                    hireTypeOccurrences.map( item => {

                        let slot      = item.resourceCostOccurrence.format( 'YYYY-Q' ),
                            value     = item.quarterlyCompensation,
                            slotIndex = _.findIndex( yearSlotsHash, { slot } );

                        if( value > 0 ){

                            yearSlotsHash[ slotIndex ] = { ...yearSlotsHash[ slotIndex ], value };

                        }
                    } );

                    // Aggregate annual slot costs
                    years.map( year => {

                        // aggregate activity yearly total
                        let yearlyTotal = sumByAttribute( _.filter( yearSlotsHash, timeSlot => {

                            return timeSlot.slot.includes( year ) && ( timeSlot.slot != year );

                        } ), 'value' );

                        // if we have a positive total, add into the hash
                        if( yearlyTotal > 0 ){

                            let slotIndex = _.findIndex( yearSlotsHash, { slot: year } );

                            yearSlotsHash[ slotIndex ] = { ...yearSlotsHash[ slotIndex ], value: yearlyTotal };
                        }

                    } );

                    let projectActivityName   = formatLabelString( !isUserDefined ? !isCorvalManaged ? projectActivity.name : projectActivity.activity.displayName : EMPTY_VALUE_DISPLAY ),
                        hireName              = formatLabelString( delegateHire[ projectHireField ].hireType.name ),
                        hireNote              = formatLabelString( !_.isEmpty( delegateHire[ projectHireField ].note ) ? delegateHire[ projectHireField ].note : EMPTY_VALUE_DISPLAY ),
                        hireCount             = delegateHire[ projectHireField ].headcount,
                        categoryName          = formatLabelString( category?.displayName ?? 'User' ),
                        subcategoryName       = formatLabelString( subCategory?.displayName ?? 'Custom' ),
                        startingDate          = moment( hiringDate, TimeService.getDisplayDateFormat() ),
                        endDate               = moment.max( _.pluck( hireTypeOccurrences, 'resourceCostOccurrence' ) ).endOf( 'quarter' ),
                        startingYear          = startingDate.format( 'YYYY' ),
                        startingQuarter       = startingDate.format( 'Q' ),
                        quarterCount          = TimeService.getQuarterCount( startingDate, endDate ),
                        quarterlyCompensation = roundToPrecision( delegateHire.quarterlyCompensation );

                    let activityRowValues = _.map( yearSlotsHash, slot => slot.value ).map( n => _.isNumber( n ) ? roundToPrecision( n ) : n ),
                        activityRowData   = [
                            'Resource Hire',
                            businessFunction.name,
                            categoryName,
                            subcategoryName,
                            hireName,
                            hiringDate,
                            startingYear,
                            startingQuarter,
                            quarterCount,
                            quarterlyCompensation,
                            hireCount,
                            projectActivityName,
                            hireNote,
                            ...activityRowValues
                        ] as CSVDataRowType;

                    // Push activity row
                    CSVRows.push( activityRowData );

                    CSVRowSubtotals.push( activityRowValues );
                } );
            }

        } );

        // Get full totals of all resources
        CSVRowTotals = sortedBudgetData.map( expandCostOccurrenceTotals );

        // Aggregate professional fee totals
        let hiringTotals = totalColumnizedValuesByYearSlot( CSVRowTotals );
        let hiringSubtotals = totalColumnizedValuesByYearSlot( CSVRowSubtotals );

        // Aggregate totals
        let subtotals = totalColumnizedValuesByYearSlot( [ hiringSubtotals ] );
        let totals = totalColumnizedValuesByYearSlot( [ hiringTotals ] );

        // filler array for total rows equal to amout of columns in CSVHeaders, less the row type and all the year slot columns
        let totalRowsNonNumericFill = _.range( CSVHeaders.length - ( 1 + yearSlots.length ) ).map( () => emptyValue );

        // // push total / subtotal row
        // CSVRows.push( [ 'Subtotals', ...totalRowsNonNumericFill, ...subtotals ] );
        // CSVRows.push( [ 'Totals', ...totalRowsNonNumericFill, ...totals ] );

        // stringify rows
        CSVData = CSVRows.join( '\n' );

        return [ CSVData, CSVRows ];
    };

    return {
        getDefaultFilters,
        getInitialUserFilters,
        hasFiltersApplied,
        businessFunctionFilterMethod,
        tagFilterMethod,
        hireTypeFilterMethod,
        getExpandedRows,
        isRowExpanded,
        generateAnnualMapping,
        getAnnualMappingOutsideTimeframes,
        generateQuarterColumnID,
        generateYearlyColumnID,
        getProjectYears,
        getYearsOfType,
        getHiddenTimeColumns,
        getActiveYearsFromHiddenColumns,
        getQuarterlyColumns,
        getQuarterlyColumnIds,
        handleYearExpansionToggled,
        handleAllYearExpansionToggled,
        isYearActive,
        generateTotalsFromAnnualizedMapping,
        hasCostAdjustments,
        hasTimeframeRange,
        getProjectHireSubTotalForTimePeriod,
        getProjectCustomBudgetItemsSubTotalForTimePeriod,
        rawCategoryBudgetDataToCSVFormat,
        rawHiringBudgetDataToCSVFormat,
        generateTotalOverQuartersUsingMethod,
    } as UseProjectBudgetVisualizationHook<D>;
}
